/**
 * Salesforce Commerce Cloud Image Transformation Utilities
 * Based on: https://help.salesforce.com/s/articleView?id=cc.b2c_creating_image_transformation_urls.htm
 */

const DEV_IMAGE_HOST = "https://hsa.health-e-commerce.workers.dev";

/**
 * Matches SFCC product/content image paths — the static image servlet
 * (`/on/demandware.static/...`) and the dynamic image service REST endpoint
 * (`/dw/image/v2/{realm}/...`). Used to decide whether an *absolute* link
 * should be re-pointed at the storefront host (below).
 */
const SFCC_IMAGE_PATH = /\/(?:on\/demandware\.static|dw\/image)\//;

const STATIC_SERVLET_PATH = "/on/demandware.static/";

/** Captures the DIS folder id out of `/dw/image/v2/{folderId}/…`. */
const DIS_PREFIX_RE = /\/dw\/image\/v2\/([^/]+)/;

/**
 * Inject the SFCC Dynamic Imaging Service (DIS) transformation prefix
 * (`/dw/image/v2/{imageFolderId}`) ahead of the raw static image path so the
 * platform actually honours the `sw`/`sh`/`sm`/`q`/… transform params.
 *
 * Without it, the bare `/on/demandware.static/` servlet ignores every param and
 * serves the full-size original (verified: ~398 KB vs ~8 KB for `sw=320`). SFRA
 * emits the DIS form natively via `mediaFile.getAbsImageURL()`; we can't call
 * that in the Worker, so we build the prefix ourselves.
 *
 * An already-DIS path is NORMALISED to `imageFolderId` rather than passed
 * through. The folder id must match the serving instance or SFCC 404s, and
 * upstreams do supply the wrong one: the CIO index emitted
 * `/dw/image/v2/BFKW_STG/…` to the dev storefront, so every PLP tile 404'd
 * (396 of 396 on `/best-sellers`) while SCAPI-sourced PDP images — which arrive
 * as bare static paths and get the id injected here — were correct. Re-hosting
 * fixed the host and left the stale id, which is the one combination guaranteed
 * to fail. Normalising here makes the storefront immune to that class of feed
 * drift regardless of which upstream is wrong.
 *
 * Idempotent + SFCC-static-only — returns the path unchanged when:
 * - `imageFolderId` is empty (DIS not routed for this env → raw static fallback)
 * - the path is already a DIS URL for THIS instance's folder id
 * - it isn't the `/on/demandware.static/` servlet (Builder CDN, YouTube, …)
 */
export function toDisImagePath(
  pathname: string,
  imageFolderId: string | undefined,
): string {
  if (!imageFolderId) return pathname;

  // Already a DIS path: swap a mismatched folder id for this instance's, and
  // return the identical string when it already matches (idempotence).
  const existing = pathname.match(DIS_PREFIX_RE);
  if (existing) {
    return existing[1] === imageFolderId
      ? pathname
      : pathname.replace(existing[0], `/dw/image/v2/${imageFolderId}`);
  }
  // A `/dw/image/` path that isn't the `v2/{folder}` form is left alone — we
  // have no id to correct and rewriting blind would break a shape we don't know.
  if (pathname.includes("/dw/image/")) return pathname;

  const idx = pathname.indexOf(STATIC_SERVLET_PATH);
  if (idx === -1) return pathname;
  return `${pathname.slice(0, idx)}/dw/image/v2/${imageFolderId}${pathname.slice(idx)}`;
}

/**
 * Canonical SFCC image-URL host resolver — the single place host resolution
 * happens, server- and client-side alike.
 *
 * SCAPI returns image links on the raw instance/realm host
 * (`*.commercecloud.salesforce.com`), which serves the API — NOT product
 * images: it 403s with `text/html` and Chrome ORB-blocks the `<img>`. Only the
 * storefront host (`IMAGES_HOST`, e.g. `www.hsastore.com`) serves the image
 * servlet + DIS. CIO already returns relative paths, so PLP tiles work; SCAPI's
 * absolute links on PDP/QuickView/SEO/analytics do not. Re-point them here.
 *
 * - relative (`/on/demandware.static/...`) → prepend `imagesHost`
 * - absolute SFCC image link → rebuild `${imagesHost}${pathname}${search}`
 *   (pathname preserved — both servlet + `/dw/image/v2/` forms 200 on the host)
 * - absolute non-SFCC link (Builder CDN, YouTube thumb, …) → passthrough
 * - empty / unparseable / no `imagesHost` → passthrough (never throws)
 *
 * When `imageFolderId` is supplied, SFCC static paths are also upgraded to the
 * DIS transformation route (see {@link toDisImagePath}) so transform params are
 * honoured. Omit it (or pass "") to keep the raw static behaviour unchanged.
 */
export function resolveImageHost(
  link: string | undefined,
  imagesHost: string | undefined,
  imageFolderId?: string,
): string | undefined {
  if (!link) return link;
  if (link.charAt(0) === "/") {
    const path = toDisImagePath(link, imageFolderId);
    return imagesHost ? `${imagesHost}${path}` : path;
  }
  if (!imagesHost) return link;
  try {
    const url = new URL(link);
    if (SFCC_IMAGE_PATH.test(url.pathname)) {
      return `${imagesHost}${toDisImagePath(url.pathname, imageFolderId)}${url.search}`;
    }
  } catch {
    // Not an absolute URL we can parse — leave it untouched.
  }
  return link;
}

/**
 * Resolves a potentially relative SFCC image URL to an absolute one.
 * On localhost, relative paths (e.g. /on/demandware.static/...) are prefixed
 * with the dev Workers host so images load correctly without a local SFRA proxy.
 */
export function resolveImageUrl(
  imageUrl: string | undefined,
): string | undefined {
  if (!imageUrl || imageUrl.startsWith("http")) return imageUrl;
  if (typeof window !== "undefined") {
    const { hostname } = window.location;
    const isLocalhost =
      hostname === "localhost" || hostname.endsWith(".localhost");
    if (isLocalhost) return `${DEV_IMAGE_HOST}${imageUrl}`;
  }
  return imageUrl;
}

export type ScaleMode = "fit" | "cut";
export type ImageFormat = "webp" | "jpg" | "png" | "auto";

export interface ImageTransformParams {
  width?: number;
  height?: number;
  scaleMode?: ScaleMode;
  quality?: number;
  format?: ImageFormat;
  strip?: boolean;
  bgcolor?: string;
  dpr?: number; // Device pixel ratio for retina
}

export interface ResponsiveImageConfig {
  mobile: { width: number; height?: number };
  tablet: { width: number; height?: number };
  desktop: { width: number; height?: number };
  quality?: number;
  format?: ImageFormat;
}

/**
 * Generate Salesforce image transformation parameters
 */
export function generateImageParams(
  params: ImageTransformParams,
): Record<string, string> {
  const transformParams: Record<string, string> = {};

  // Size parameters
  if (params.width) {
    transformParams.sw = params.width.toString();
  }

  if (params.height) {
    transformParams.sh = params.height.toString();
  }

  // Scale mode - default to 'fit' for maintaining aspect ratio
  transformParams.sm = params.scaleMode || "fit";

  // Handle retina displays by doubling dimensions
  if (params.dpr && params.dpr > 1) {
    if (params.width) {
      transformParams.sw = (params.width * params.dpr).toString();
    }
    if (params.height) {
      transformParams.sh = (params.height * params.dpr).toString();
    }
    // Keep scale mode as fit or cut, don't change for retina
  }

  // Quality - default to 85 for optimal balance
  transformParams.q = (params.quality || 85).toString();

  // Format - default to auto for browser optimization
  if (params.format) {
    transformParams.fmt = params.format;
  }

  // Strip metadata to reduce file size
  if (params.strip !== false) {
    transformParams.strip = "true";
  }

  // Background color for transparent images
  if (params.bgcolor) {
    transformParams.bgcolor = params.bgcolor;
  }

  return transformParams;
}

/**
 * Generate srcset for responsive images
 */
export function generateSrcSet(
  baseUrl: string,
  sizes: { width: number; dpr?: number }[],
  baseParams?: Omit<ImageTransformParams, "width" | "dpr">,
): string {
  return sizes
    .map(size => {
      const params = generateImageParams({
        ...baseParams,
        width: size.width,
        dpr: size.dpr || 1,
      });

      const queryString = new URLSearchParams(params).toString();
      const descriptor = size.dpr && size.dpr > 1 ? ` ${size.dpr}x` : " 1x";

      return `${baseUrl}?${queryString}${descriptor}`;
    })
    .join(", ");
}

/**
 * Get optimized parameters for product listing page (PLP)
 */
export function getPLPImageParams(
  viewport: "mobile" | "tablet" | "desktop",
  isRetina = false,
): ImageTransformParams {
  const configs = {
    mobile: { width: 280, quality: 80 }, // For mobile tiles
    tablet: { width: 320, quality: 85 }, // For 2-3 column grids
    desktop: { width: 350, quality: 85 }, // For 3-4 column grids
  };

  const config = configs[viewport];

  return {
    width: config.width,
    height: config.width, // Square aspect ratio for PLP
    scaleMode: "fit",
    quality: config.quality,
    format: "auto",
    strip: true,
    dpr: isRetina ? 2 : 1,
  };
}

/**
 * Get optimized parameters for product detail page (PDP)
 */
export function getPDPImageParams(
  viewport: "mobile" | "tablet" | "desktop",
  isRetina = false,
): ImageTransformParams {
  const configs = {
    mobile: { width: 500, quality: 85 },
    tablet: { width: 800, quality: 90 },
    desktop: { width: 1200, quality: 90 },
  };

  const config = configs[viewport];

  return {
    width: config.width,
    scaleMode: "fit",
    quality: config.quality,
    format: "auto",
    strip: true,
    dpr: isRetina ? 2 : 1,
  };
}

/**
 * Get thumbnail parameters
 */
export function getThumbnailParams(size = 100): ImageTransformParams {
  return {
    width: size,
    height: size,
    scaleMode: "cut", // Exact dimensions for thumbnails
    quality: 75,
    format: "auto",
    strip: true,
  };
}

/**
 * Generate picture element sources for optimal loading
 */
export function generatePictureSources(
  baseUrl: string,
  type: "plp" | "pdp" | "thumbnail",
): Array<{ srcset: string; media?: string; type?: string }> {
  const sources: Array<{ srcset: string; media?: string; type?: string }> = [];

  if (type === "plp") {
    // Desktop sources - optimized for 3-4 column grid
    sources.push({
      srcset: generateSrcSet(
        baseUrl,
        [
          { width: 350, dpr: 1 },
          { width: 350, dpr: 2 },
        ],
        { scaleMode: "fit", quality: 85, format: "webp" },
      ),
      media: "(min-width: 1024px)",
      type: "image/webp",
    });

    // Tablet sources - optimized for 2-3 column grid
    sources.push({
      srcset: generateSrcSet(
        baseUrl,
        [
          { width: 320, dpr: 1 },
          { width: 320, dpr: 2 },
        ],
        { scaleMode: "fit", quality: 85, format: "webp" },
      ),
      media: "(min-width: 768px)",
      type: "image/webp",
    });

    // Mobile sources - optimized for single column or 2 column grid
    sources.push({
      srcset: generateSrcSet(
        baseUrl,
        [
          { width: 280, dpr: 1 },
          { width: 280, dpr: 2 },
        ],
        { scaleMode: "fit", quality: 80, format: "webp" },
      ),
      type: "image/webp",
    });
  } else if (type === "pdp") {
    // Desktop sources
    sources.push({
      srcset: generateSrcSet(
        baseUrl,
        [
          { width: 1200, dpr: 1 },
          { width: 1200, dpr: 2 },
        ],
        { scaleMode: "fit", quality: 90, format: "webp" },
      ),
      media: "(min-width: 1024px)",
      type: "image/webp",
    });

    // Tablet sources
    sources.push({
      srcset: generateSrcSet(
        baseUrl,
        [
          { width: 800, dpr: 1 },
          { width: 800, dpr: 2 },
        ],
        { scaleMode: "fit", quality: 90, format: "webp" },
      ),
      media: "(min-width: 768px)",
      type: "image/webp",
    });

    // Mobile sources
    sources.push({
      srcset: generateSrcSet(
        baseUrl,
        [
          { width: 500, dpr: 1 },
          { width: 500, dpr: 2 },
        ],
        { scaleMode: "fit", quality: 85, format: "webp" },
      ),
      type: "image/webp",
    });
  }

  return sources;
}
